import { useEffect, useMemo, useRef, useState } from "react"
import { Card, Container } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import html2canvas from "html2canvas"
import { jsPDF } from "jspdf"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import moment from "moment"

import API from "../common/API"
import { FormatMoney } from "../common/helpers"
import Filter from "../components/core/Filter/Filter"
import CRUDField from "../components/core/genericCRUD/CRUDField"

type SortDirection = "asc" | "desc"

type ReportColumn = {
    key: string
    label: string
    type?: "text" | "number" | "money" | "date" | "datetime" | "boolean"
    className?: string
    sortable?: boolean
    render?: (value: unknown, row: Record<string, unknown>) => React.ReactNode
    sortValue?: (row: Record<string, unknown>) => string | number
    exportValue?: (row: Record<string, unknown>) => string | number | boolean
}

type ReportSummaryField = {
    name: string
    title: string
    type: "text" | "number" | "money"
    className?: string
    colspan?: number
}

interface ReportComponentProps {
    title?: string
    endpoint: string
    columns: ReportColumn[]
    filterFields?: CRUDField[]
    groupBy?: string | null
    summaryFields?: ReportSummaryField[]
    fileName?: string
    showIndex?: boolean
}

const ReportComponent: React.FC<ReportComponentProps> = ({
    title,
    endpoint,
    columns,
    filterFields = [],
    groupBy = null,
    summaryFields = [],
    fileName,
    showIndex = true,
}) => {
    const { t } = useTranslation("global")
    const [filters, setFilters] = useState<Record<string, unknown>>({})
    const [records, setRecords] = useState<Record<string, unknown>[]>([])
    const [loading, setLoading] = useState(false)
    const [sortKey, setSortKey] = useState<string | null>(null)
    const [sortDir, setSortDir] = useState<SortDirection>("asc")
    const [exportingPdf, setExportingPdf] = useState(false)
    const contentRef = useRef<HTMLDivElement | null>(null)

    const resolvedFileName = fileName || title || "report"

    const buildQuery = () => {
        if (!filterFields.length) return ""
        return filterFields
            .map(item => {
                const value = filters[item.name]
                return `${item.name}=${value ?? ""}`
            })
            .join("&")
    }

    const fetchRecords = async () => {
        try {
            setLoading(true)
            const query = buildQuery()
            const querySuffix = query
                ? `${endpoint.includes("?") ? "&" : "?"}${query}`
                : ""
            const request = await API.getAction(`${endpoint}${querySuffix}`)
            if (request.data.status === "ok") {
                setRecords(request.data.response || [])
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchRecords()
    }, [filters])

    useEffect(() => {
        fetchRecords()
    }, [])

    const updateSort = (key: string) => {
        if (sortKey === key) {
            setSortDir(prev => (prev === "asc" ? "desc" : "asc"))
            return
        }
        setSortKey(key)
        setSortDir("asc")
    }

    const getColumn = (key: string) => columns.find(col => col.key === key)

    const getSortValue = (row: Record<string, unknown>, col: ReportColumn) => {
        if (col.sortValue) return col.sortValue(row)
        const value = row[col.key]
        if (col.type === "number" || col.type === "money") {
            return Number(value ?? 0)
        }
        if (col.type === "date" || col.type === "datetime") {
            return value ? new Date(String(value)).getTime() : 0
        }
        if (col.type === "boolean") {
            return value ? 1 : 0
        }
        return String(value ?? "").toUpperCase()
    }

    const sortedRecords = useMemo(() => {
        if (!sortKey) return records
        const col = getColumn(sortKey)
        if (!col) return records
        return [...records].sort((a, b) => {
            const aValue = getSortValue(a, col)
            const bValue = getSortValue(b, col)
            if (aValue < bValue) return sortDir === "asc" ? -1 : 1
            if (aValue > bValue) return sortDir === "asc" ? 1 : -1
            return 0
        })
    }, [records, sortKey, sortDir, columns])

    const groupRecords = (
        items: Record<string, unknown>[]
    ): Record<string, Record<string, unknown>[]> => {
        return items.reduce<Record<string, Record<string, unknown>[]>>(
            (acc, item) => {
                const key = String(item[groupBy as string] ?? "-")
                if (!acc[key]) acc[key] = []
                acc[key].push(item)
                return acc
            },
            {}
        )
    }

    const groupedRecords = useMemo(
        () => (groupBy ? groupRecords(sortedRecords) : null),
        [sortedRecords, groupBy]
    )

    const formatValue = (
        col: ReportColumn,
        item: Record<string, unknown>
    ): React.ReactNode => {
        const value: any = item[col.key]
        if (col.render) return col.render(value, item)
        switch (col.type) {
            case "money":
                return FormatMoney(Number(value ?? 0))
            case "date":
                return value ? moment(String(value)).format("MMM DD, YYYY") : "-"
            case "datetime":
                return value ? moment(String(value)).format("MMM DD, YYYY - hh:mm A") : "-"
            case "boolean":
                return value ? t("Yes") : t("No")
            default:
                return value ?? ""
        }
    }

    const buildSummary = (items: Record<string, unknown>[]) => {
        const summary: Record<string, number> = {}
        summaryFields.forEach(field => {
            summary[field.name] = 0
        })
        items.forEach(item => {
            summaryFields.forEach(field => {
                const value = Number(item[field.name] ?? 0)
                if (!Number.isNaN(value)) summary[field.name] += value
            })
        })
        return summary
    }

    const formatSummary = (field: ReportSummaryField, summary: Record<string, number>) => {
        switch (field.type) {
            case "money":
                return FormatMoney(summary[field.name] ?? 0)
            case "number":
                return summary[field.name] ?? 0
            default:
                return field.title
        }
    }

    const getExportValue = (row: Record<string, unknown>, col: ReportColumn) => {
        if (col.exportValue) return col.exportValue(row)
        const value = row[col.key]
        if (col.type === "date") {
            return value ? moment(String(value)).format("YYYY-MM-DD") : ""
        }
        if (col.type === "datetime") {
            return value ? moment(String(value)).format("YYYY-MM-DD HH:mm") : ""
        }
        if (col.type === "money" || col.type === "number") {
            return Number(value ?? 0)
        }
        if (col.type === "boolean") {
            return value ? "Yes" : "No"
        }
        return value ?? ""
    }

    const buildExportRows = () => {
        return sortedRecords.map(row => {
            const obj: Record<string, unknown> = {}
            columns.forEach(col => {
                obj[col.label] = getExportValue(row, col)
            })
            return obj
        })
    }

    const downloadCsv = () => {
        const rows = buildExportRows()
        const worksheet = XLSX.utils.json_to_sheet(rows)
        const csv = XLSX.utils.sheet_to_csv(worksheet)
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
        saveAs(blob, `${resolvedFileName}.csv`)
    }

    const downloadExcel = () => {
        const rows = buildExportRows()
        const worksheet = XLSX.utils.json_to_sheet(rows)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report")
        const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
        const blob = new Blob([buffer], {
            type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
        saveAs(blob, `${resolvedFileName}.xlsx`)
    }

    const downloadPdf = async () => {
        const element = contentRef.current
        if (!element) return
        setExportingPdf(true)
        try {
            const canvas = await html2canvas(element, { scale: 2, useCORS: true })
            const imgData = canvas.toDataURL("image/png")
            const pdf = new jsPDF("p", "pt", "a4")
            const pageWidth = pdf.internal.pageSize.getWidth()
            const pageHeight = pdf.internal.pageSize.getHeight()
            const imgHeight = (canvas.height * pageWidth) / canvas.width
            let heightLeft = imgHeight
            let position = 0

            pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight)
            heightLeft -= pageHeight

            while (heightLeft > 0) {
                position = heightLeft - imgHeight
                pdf.addPage()
                pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight)
                heightLeft -= pageHeight
            }

            pdf.save(`${resolvedFileName}.pdf`)
        } finally {
            setExportingPdf(false)
        }
    }

    const tableUI = (items: Record<string, unknown>[]) => {
        const summary = buildSummary(items)
        return (
            <div className="table-responsive">
                <table className="table table-striped table-bordered">
                    <thead>
                        <tr>
                            {showIndex && <th>#</th>}
                            {columns.map(col => {
                                const isActive = sortKey === col.key
                                return (
                                    <th
                                        key={col.key}
                                        className={col.className}
                                        role={col.sortable ? "button" : undefined}
                                        onClick={() => col.sortable && updateSort(col.key)}
                                    >
                                        {col.label}
                                        {col.sortable && (
                                            <i
                                                className={`fa ms-2 text-muted ${isActive
                                                    ? sortDir === "asc"
                                                        ? "fa-sort-up"
                                                        : "fa-sort-down"
                                                    : "fa-sort"
                                                    }`}
                                            />
                                        )}
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={columns.length + (showIndex ? 1 : 0)}
                                    className="text-center py-4"
                                >
                                    <span className="spinner-border text-primary" />
                                </td>
                            </tr>
                        ) : items.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={columns.length + (showIndex ? 1 : 0)}
                                    className="text-center text-muted py-4"
                                >
                                    {t("No data available")}
                                </td>
                            </tr>
                        ) : (
                            items.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {showIndex && <td>{rowIndex + 1}</td>}
                                    {columns.map(col => (
                                        <td key={col.key} className={col.className}>
                                            {formatValue(col, row)}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                    {summaryFields.length > 0 && !loading && items.length > 0 && (
                        <tfoot>
                            <tr>
                                {showIndex && <td></td>}
                                {summaryFields.map(field => (
                                    <th
                                        key={field.name}
                                        className={field.className}
                                        colSpan={field.colspan}
                                    >
                                        {formatSummary(field, summary)}
                                    </th>
                                ))}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        )
    }

    return (
        <Container fluid>
            <Card className="my-4">
                <Card.Header className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                    <Card.Title className="mb-0">{title || t("Report")}</Card.Title>
                    <div className="d-flex flex-wrap gap-2">
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            type="button"
                            onClick={downloadCsv}
                        >
                            <i className="fa-solid fa-file-csv"></i> CSV
                        </button>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            type="button"
                            onClick={downloadExcel}
                        >
                            <i className="fa-solid fa-file-excel"></i> Excel
                        </button>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            type="button"
                            onClick={downloadPdf}
                            disabled={exportingPdf}
                        >
                            <i className="fa-solid fa-file-pdf"></i> PDF
                        </button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {filterFields.length > 0 && (
                        <Filter
                            filterAction={fetchRecords}
                            fields={filterFields}
                            setFilterFields={setFilters}
                        />
                    )}
                    <div ref={contentRef} id="report-content">
                        {groupBy && groupedRecords
                            ? Object.keys(groupedRecords).map(group => (
                                <div key={group} className="mb-4">
                                    <h5 className="mt-3">{group}</h5>
                                    {tableUI(groupedRecords[group])}
                                </div>
                            ))
                            : tableUI(sortedRecords)}
                    </div>
                </Card.Body>
            </Card>
        </Container>
    )
}

export default ReportComponent
